<script>
  import { onMount } from 'svelte';
  import { fade, fly, scale } from 'svelte/transition';
  import { spring } from 'svelte/motion';

  // Projects data
  const projects = [
    {
      title: 'Project 1',
      description: 'A web application built with React and Node.js',
      tags: ['React', 'Node.js', 'MongoDB'],
      link: '#',
      image: 'https://via.placeholder.com/300x200'
    },
    {
      title: 'Project 2',
      description: 'Mobile app developed with Flutter',
      tags: ['Flutter', 'Dart', 'Firebase'],
      link: '#',
      image: 'https://via.placeholder.com/300x200'
    },
    {
      title: 'Project 3',
      description: 'Machine learning model for data analysis',
      tags: ['Python', 'TensorFlow', 'Scikit-learn'],
      link: '#',
      image: 'https://via.placeholder.com/300x200'
    }
  ];

  // Skills data
  const skills = [
    { name: 'Python', level: 90 },
    { name: 'Java', level: 85 },
    { name: 'C', level: 80 },
    { name: 'JavaScript', level: 88 },
    { name: 'HTML/CSS', level: 92 },
    { name: 'React', level: 85 },
    { name: 'Node.js', level: 80 }
  ];

  // Animation for skills bars
  let skillsVisible = false;
  let coordinates = spring({ x: 0, y: 0 });

  onMount(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        skillsVisible = true;
      }
    });
    
    const skillsSection = document.querySelector('#skills');
    if (skillsSection) observer.observe(skillsSection);

    return () => {
      if (skillsSection) observer.unobserve(skillsSection);
    };
  });

  // Mouse move effect for hero section
  function handleMousemove(event) {
    coordinates.set({
      x: event.clientX / 20,
      y: event.clientY / 20
    });
  }
</script>

<svelte:head>
  <title>Neha Joshi | Portfolio</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
</svelte:head>

<main>
  <!-- Hero Section -->
  <section class="hero" on:mousemove={handleMousemove}>
    <div class="hero-content">
      <div class="hero-image-container" 
        style="transform: translate({$coordinates.x}px, {$coordinates.y}px)">
        <div class="hero-image">NJ</div>
      </div>
      <h1 in:fly={{ y: 50, duration: 1000 }}>Neha Joshi</h1>
      <h2 in:fly={{ y: 50, duration: 1000, delay: 300 }}>
        Computer Science Undergraduate
      </h2>
      <p in:fly={{ y: 50, duration: 1000, delay: 600 }}>
        University of Maryland, College Park
      </p>
      <div class="hero-buttons" in:fly={{ y: 50, duration: 1000, delay: 900 }}>
        <a href="#projects" class="button primary">View Projects</a>
        <a href="#contact" class="button secondary">Contact Me</a>
      </div>
    </div>
    <div class="hero-background">
      <div class="gradient-blob blob-1"></div>
      <div class="gradient-blob blob-2"></div>
    </div>
  </section>

  <!-- About Section -->
  <section id="about" class="about">
    <div class="container">
      <h2 class="section-title">About Me</h2>
      <div class="about-content">
        <div class="about-text">
          <p>
            I'm a computer science undergraduate at the University of Maryland, College Park, with 
            experience in software development, web development, and programming languages 
            including Python, Java, and C.
          </p>
          <p>
            My passion lies in creating efficient and user-friendly applications that solve 
            real-world problems. I'm constantly learning and exploring new technologies to 
            expand my skillset and stay current with industry trends.
          </p>
        </div>
        <div id="skills" class="skills-container">
          <h3>Technical Skills</h3>
          <div class="skills-grid">
            {#each skills as skill}
              <div class="skill">
                <div class="skill-info">
                  <span class="skill-name">{skill.name}</span>
                  <span class="skill-percentage">{skill.level}%</span>
                </div>
                <div class="skill-bar">
                  <div class="skill-progress" 
                    style="width: {skillsVisible ? skill.level : 0}%" 
                    transition:scale={{
                      duration: 1000,
                      delay: 100 * skills.indexOf(skill),
                      easing: t => t * (2 - t)
                    }}>
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Projects Section -->
  <section id="projects" class="projects">
    <div class="container">
      <h2 class="section-title">My Projects</h2>
      <div class="projects-grid">
        {#each projects as project, i}
          <div class="project-card" 
            in:fly={{ y: 50, duration: 800, delay: 200 * i }}>
            <div class="project-image">
              <img src={project.image} alt={project.title} />
            </div>
            <div class="project-content">
              <h3>{project.title}</h3>
              <p>{project.description}</p>
              <div class="project-tags">
                {#each project.tags as tag}
                  <span class="tag">{tag}</span>
                {/each}
              </div>
              <a href={project.link} class="project-link">View Project</a>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Contact Section -->
  <section id="contact" class="contact">
    <div class="container">
      <h2 class="section-title">Get In Touch</h2>
      <div class="contact-content">
        <div class="contact-info">
          <p>
            I'm currently open to internship opportunities and collaboration 
            on interesting projects. Feel free to reach out!
          </p>
          <div class="contact-methods">
            <div class="contact-method">
              <span class="contact-label">Email:</span>
              <a href="mailto:neha@example.com">neha@example.com</a>
            </div>
            <div class="contact-method">
              <span class="contact-label">LinkedIn:</span>
              <a href="#" target="_blank">linkedin.com/in/neha_j_joshi</a>
            </div>
            <div class="contact-method">
              <span class="contact-label">GitHub:</span>
              <a href="#" target="_blank">github.com/nehajoshi9</a>
            </div>
          </div>
        </div>
        <div class="contact-form">
          <form>
            <div class="form-group">
              <label for="name">Name</label>
              <input type="text" id="name" placeholder="Your Name" />
            </div>
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" placeholder="Your Email" />
            </div>
            <div class="form-group">
              <label for="message">Message</label>
              <textarea id="message" rows="5" placeholder="Your Message"></textarea>
            </div>
            <button type="submit" class="button primary">Send Message</button>
          </form>
        </div>
      </div>
    </div>
  </section>

  <!-- Footer -->
  <footer>
    <div class="container">
      <p>&copy; {new Date().getFullYear()} Neha Joshi. All rights reserved.</p>
      <div class="footer-links">
        <a href="#" target="_blank">LinkedIn</a>
        <a href="#" target="_blank">GitHub</a>
        <a href="#" target="_blank">Twitter</a>
      </div>
    </div>
  </footer>
</main>

<style>
  :global(body) {
    margin: 0;
    padding: 0;
    font-family: 'Inter', sans-serif;
    color: #333;
    background-color: #f9fafb;
    overflow-x: hidden;
  }

  :global(*) {
    box-sizing: border-box;
  }

  main {
    width: 100%;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  section {
    padding: 6rem 0;
    position: relative;
  }

  /* Typography */
  h1, h2, h3 {
    margin: 0;
    font-weight: 700;
  }

  h1 {
    font-size: 3.5rem;
    margin-bottom: 1rem;
    background: linear-gradient(to right, #7928CA, #FF0080);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  h2 {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    color: #444;
  }

  .section-title {
    position: relative;
    display: inline-block;
    margin-bottom: 3rem;
    text-align: center;
    width: 100%;
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(to right, #7928CA, #FF0080);
    border-radius: 2px;
  }

  p {
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  /* Buttons */
  .button {
    display: inline-block;
    padding: 0.8rem 1.8rem;
    border-radius: 30px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    border: none;
    font-size: 1rem;
  }

  .primary {
    background: linear-gradient(to right, #7928CA, #FF0080);
    color: white;
    box-shadow: 0 4px 15px rgba(255, 0, 128, 0.2);
  }

  .primary:hover {
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(255, 0, 128, 0.3);
  }

  .secondary {
    background: transparent;
    color: #7928CA;
    border: 2px solid #7928CA;
  }

  .secondary:hover {
    background: rgba(121, 40, 202, 0.1);
    transform: translateY(-3px);
  }

  /* Hero Section */
  .hero {
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .hero-content {
    position: relative;
    z-index: 10;
    max-width: 800px;
    padding: 2rem;
  }

  .hero-image-container {
    margin: 0 auto 2rem;
    transition: transform 0.1s ease-out;
  }

  .hero-image {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: linear-gradient(135deg, #7928CA, #FF0080);
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 3rem;
    font-weight: bold;
    box-shadow: 0 10px 30px rgba(121, 40, 202, 0.3);
  }

  .hero-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-top: 2rem;
  }

  .hero-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  .gradient-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.4;
  }

  .blob-1 {
    width: 500px;
    height: 500px;
    background: linear-gradient(to right, #7928CA, #FF0080);
    top: -100px;
    right: -100px;
    animation: float 8s ease-in-out infinite;
  }

  .blob-2 {
    width: 400px;
    height: 400px;
    background: linear-gradient(to right, #00BFFF, #7928CA);
    bottom: -100px;
    left: -100px;
    animation: float 10s ease-in-out infinite reverse;
  }

  @keyframes float {
    0% { transform: translate(0, 0); }
    50% { transform: translate(30px, 30px); }
    100% { transform: translate(0, 0); }
  }

  /* About Section */
  .about {
    background-color: #ffffff;
    border-radius: 30px 30px 0 0;
    margin-top: -30px;
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.05);
  }

  .about-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }

  /* Skills */
  .skills-container {
    padding: 2rem;
    background: #f1f5f9;
    border-radius: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  }

  .skills-grid {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .skill-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  .skill-name {
    font-weight: 600;
  }

  .skill-percentage {
    color: #7928CA;
  }

  .skill-bar {
    height: 8px;
    background-color: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
  }

  .skill-progress {
    height: 100%;
    background: linear-gradient(to right, #7928CA, #FF0080);
    border-radius: 4px;
  }

  /* Projects Section */
  .projects {
    background: #f9fafb;
  }

  .projects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2rem;
  }

  .project-card {
    background: white;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .project-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }

  .project-image img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  .project-card:hover .project-image img {
    transform: scale(1.05);
  }

  .project-content {
    padding: 1.5rem;
  }

  .project-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1rem 0;
  }

  .tag {
    background: #f1f5f9;
    color: #7928CA;
    padding: 0.4rem 0.8rem;
    border-radius: 30px;
    font-size: 0.8rem;
    font-weight: 500;
  }

  .project-link {
    color: #7928CA;
    text-decoration: none;
    font-weight: 600;
    display: inline-block;
    margin-top: 1rem;
    position: relative;
  }

  .project-link::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(to right, #7928CA, #FF0080);
    transition: width 0.3s ease;
  }

  .project-link:hover::after {
    width: 100%;
  }

  /* Contact Section */
  .contact {
    background: white;
  }

  .contact-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }

  .contact-methods {
    margin-top: 2rem;
  }

  .contact-method {
    margin-bottom: 1rem;
  }

  .contact-label {
    font-weight: 600;
    margin-right: 0.5rem;
  }

  .contact-form form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-group label {
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .form-group input,
  .form-group textarea {
    padding: 0.8rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-family: inherit;
    transition: border-color 0.3s ease;
  }

  .form-group input:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #7928CA;
  }

  /* Footer */
  footer {
    background: #1f2937;
    color: white;
    padding: 2rem 0;
  }

  footer .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  footer p {
    margin: 0;
  }

  .footer-links {
    display: flex;
    gap: 1.5rem;
  }

  .footer-links a {
    color: white;
    text-decoration: none;
    transition: color 0.3s ease;
  }

  .footer-links a:hover {
    color: #FF0080;
  }

  /* Responsive */
  @media (max-width: 900px) {
    .about-content,
    .contact-content {
      grid-template-columns: 1fr;
    }

    h1 {
      font-size: 2.5rem;
    }
  }

  @media (max-width: 600px) {
    .hero-buttons {
      flex-direction: column;
      gap: 1rem;
    }

    .projects-grid {
      grid-template-columns: 1fr;
    }

    section {
      padding: 4rem 0;
    }
  }
</style>